/**
 * LatestAlertsStrip — the newest still-open alerts, as the FIRST thing on a dashboard.
 *
 * Everything else the dashboard shows is a count: alerts today, people on site, cameras
 * online. Counts are the answer to "how is it going"; nobody opens a security dashboard to
 * read those first. They open it to find out whether something is happening RIGHT NOW — and
 * that question is answered by the alert's own picture, not by a number that includes it.
 * So the strip sits above the analytics sheet and shows the two newest open alerts as
 * pictures, with the count reduced to a pill beside the heading.
 *
 * It is a BAND, not a hero: one shallow row of small frames, sized so the analytics below it
 * stay on screen. Being first is what gives the alerts their weight here; being enormous only
 * pushes everything else down.
 *
 * ON ITS OWN SHEET, like every other block on the dashboard. The cards used to float directly
 * on the page body with a bare heading above them, which read as loose furniture rather than a
 * section: the heading had nothing holding it to the row beneath it, and the row had no edge
 * separating it from the analytics sheet below. The same panel the other sections use — card
 * surface, border, one soft shadow — gives the heading and the pictures a single container and
 * a visible seam, and it costs the band no height beyond its own padding.
 *
 * THE PICTURE IS THE CARD. Severity, clock and the two actions ride on top of the snapshot
 * rather than beside it, and the name and place sit in a scrim along the bottom — the same
 * shape a camera view has, which is what an operator is used to reading. A card with no
 * image yet is the same size and the same dark surface with the alert's icon in the middle,
 * so a preview landing never reflows the row.
 *
 * AS MANY CARDS AS THE ROW HOLDS, per screenful: the width is measured and the cards fill it
 * — two on a laptop, five on a wide monitor — rather than a fixed count that leaves half the
 * band empty on one screen and crops the news on another. It stays a glance either way: the
 * strip never wraps to a second row, and `maxCards` stops a very wide screen from putting the
 * whole list on show at once. When nothing is open the row collapses to a single quiet line
 * rather than empty frames — "clear" should look calm, not broken.
 *
 * THE REMAINDER SLIDES, it doesn't send you away. Everything past what fit used to be one
 * dashed "+7 more" tile that navigated to the alert list — which meant the only way to see the
 * fourth-newest alert's PICTURE was to leave the dashboard, and the picture is the whole
 * reason this strip exists. So the row scrolls sideways: one continuous track of card-pitch
 * snap points, dragged with a finger, thrown with a trackpad, or walked with the arrow keys.
 *
 * SCROLLED, NOT PAGED — no standing next/prev buttons. Buttons make the row a deck of discrete
 * pages, which is a worse fit for what this is: a queue in time order that an operator sweeps
 * through, at whatever granularity they like, on hardware that already has horizontal scroll
 * gestures. What buttons DO give you is a sense of position, and losing that is the real cost
 * — so the position is stated twice instead, as a `4–6 of 13` readout in the head and as a
 * scrubber under the row. The scrubber is not decoration: it is draggable, and it is the one
 * control that says how much of the queue is off-screen before you go looking for it.
 *
 * AND THE EDGE ANSWERS THE HOVER. A row that scrolls with no scrollbar is a row that can be
 * mistaken for a row that ends — the card at the right-hand edge looks like the last alert
 * rather than the last VISIBLE one. So bringing the pointer over the row fades the far card
 * under a chevron on whichever side has more behind it: the cue appears exactly where the
 * operator's attention already is, says which way the queue continues, and scrolls a screenful
 * when clicked. It is only ever a hint — it costs nothing when ignored, it is not there when
 * there is nothing past it, and it appears on keyboard focus too so it isn't a mouse-only
 * affordance.
 *
 * PREVIEWS ARE FETCHED ONLY FOR CARDS THAT COME INTO VIEW. The whole loaded feed is in the
 * track's DOM — that is what makes the scroll continuous — but a card resolves its picture the
 * first time it comes within a screen's edge of being looked at, and never before (on the
 * cloud every one of those is a blob through the tunnel). It then keeps it, so sweeping back
 * and forth over the same alerts costs nothing. Pages past the live feed are pulled through
 * `onLoadMore` as the track nears its end — see `useLatestAlerts` for the request guards — and
 * the cells they will fill stand in as placeholders while they are on the way.
 *
 * AND UNDER THE PICTURES, A TIMELINE. The cards answer "what is happening"; what they cannot
 * show is ORDER — that four of these landed inside a minute of each other, or that one has been
 * sitting since the night shift — and order is what turns a handful of alerts into an incident.
 * So the newest three repeat as a rail beneath the row, severity word and clock reading per
 * entry, in a fraction of the height one card takes. It replaced a band on the analytics sheet below that answered
 * the same question from the summary: two answers to one question on one screen, and this is
 * the one holding the alerts themselves.
 *
 * AND IT FILTERS BY SEVERITY, from a segmented track in the head. A queue in strict time order
 * is the right default and the wrong thing to be stuck with: the alert that matters is rarely
 * the newest one, and on a busy node the criticals are buried among warnings within a minute of
 * arriving. The chips carry their own counts, so "two criticals" is read before anything is
 * clicked. Filtering narrows what is DRAWN, never what is LOADED — and a chip that matches
 * nothing in the live feed sends the row looking further back, bounded by `FILTER_SCAN_MAX`.
 * The chips are the PANEL's filter, not the row's: the cards and the timeline narrow together.
 *
 * The two actions are the two an operator can take without reading anything else:
 * ACKNOWLEDGE ("I'm on it") and RESOLVE ("this is dealt with"). Both are optimistic — the
 * card leaves at once and the next open alert takes its place — and both are hidden when the
 * node doesn't implement them. Anything needing a note, an assignee or a judgement belongs on
 * the alert record, which the card itself opens.
 *
 * Preview sourcing matches AlertCard's, because the same alerts are drawn by both: a direct
 * `snapshotUrl` (local node) renders immediately; otherwise `loadPreview` resolves lazily
 * (cloud blob-fetches the node's image through the tunnel) and the card renders at once
 * without waiting for it.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  ArrowRightIcon,
  ArrowTrendingDownIcon,
  BellAlertIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FireIcon,
  FunnelIcon,
  HandRaisedIcon,
  PhotoIcon,
  ShieldExclamationIcon,
  TruckIcon,
  UserIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import Tooltip from '../common/Tooltip';
import type { HexAlert } from '../../api/contracts';

/**
 * Severity, as a filled badge — the one place on this page where a solid block of alert
 * colour is right. The panel lists these by the dozen and tones them down for exactly that
 * reason; here there are two, over dark video, and the badge is competing with the picture
 * rather than with fifty siblings.
 */
const SEV: Record<string, { badge: string; ring: string; label: string; dot: string; text: string }> = {
  // `dot` and `text` are the same three severities OFF the picture — the timeline's markers and
  // its severity words. Amber goes a stop darker as text: `amber-500` is legible as a filled
  // badge and not as 9px type on the card surface.
  critical: {
    badge: 'bg-red-600 text-white',
    ring: 'ring-red-500/60',
    label: 'Critical',
    dot: 'bg-red-600',
    text: 'text-red-600',
  },
  warning: {
    badge: 'bg-amber-500 text-amber-950',
    ring: 'ring-amber-400/50',
    label: 'Warning',
    dot: 'bg-amber-500',
    text: 'text-amber-600',
  },
  normal: {
    badge: 'bg-blue-600 text-white',
    ring: 'ring-blue-500/40',
    label: 'Info',
    dot: 'bg-blue-600',
    text: 'text-blue-600',
  },
};

/**
 * The head's severity filter, as an ordered vocabulary.
 *
 * ALL FOUR ARE ALWAYS DRAWN, including the ones standing at zero. A filter row that hides its
 * empty options is a filter row that changes shape as alerts arrive, and "Critical 0" is the
 * single most useful thing this strip can say — it is the reading an operator came to the
 * dashboard for, and dropping the chip makes them infer it from an absence.
 *
 * The DOT carries the same colour the cards' badges use, so the chip and the alerts it selects
 * are recognisably about the same field. "Info" rather than "Normal", matching the badge — the
 * wire tier is `normal`, the word an operator reads is not.
 */
const SEV_FILTERS: Array<{ key: SevFilter; label: string; dot: string }> = [
  { key: 'all', label: 'All', dot: '' },
  ...(['critical', 'warning', 'normal'] as const).map((k) => ({
    key: k as SevFilter,
    label: SEV[k].label,
    dot: SEV[k].dot,
  })),
];

type SevFilter = 'all' | HexAlert['severity'];

/**
 * How far back the strip will page BY ITSELF to fill a filtered row.
 *
 * Choosing "Critical" on a row holding none is a request to look past the live feed, and the
 * strip answers it by pulling pages until something matches — but "look further back" is not
 * "download the queue", and on a node with a thousand open alerts an unbounded scan is exactly
 * that. Past this many loaded alerts the row stops pulling on its own and waits to be scrolled,
 * which is the operator saying they do want more.
 */
const FILTER_SCAN_MAX = 120;

/** How many rows the timeline under the row draws. Three: the pictures above are the glance and
 *  this is the sequence behind them, and past three the rail stops being read at a glance and
 *  starts being the alert list — which is one click away and better at it. */
const TIMELINE_ROWS = 3;

/**
 * The TIMELINE under the card row — the same alerts as a sequence rather than as pictures.
 *
 * The cards answer "what is happening"; they are wide, they scroll, and three of them is all a
 * band has room for. What they cannot show is ORDER — that these four landed inside a minute of
 * each other and this one has been sitting since the night shift — and order is what turns a
 * handful of alerts into an incident. A rail says "then, then, then" with no words spent on it,
 * and it fits five rows in the height one card takes.
 *
 * IT READS THE SAME FILTERED LIST THE ROW DOES. The chips in the head are the panel's filter,
 * not the row's: choosing Critical narrows the pictures and the sequence together, because a
 * timeline that kept showing warnings under a row of criticals would be answering a question
 * nobody asked.
 *
 * THE SEVERITY IS A WORD, in its own column, not just the colour of the dot — the list is mixed
 * by default, and three severities told apart by three small dots is a legend the page does not
 * carry. Colour and word together is also the only version that survives a monochrome screen or
 * a red-green reader. Fixed width and right-aligned, so the markers stack into one clean edge.
 *
 * ABSOLUTE TIME, not "4m ago". The cards already carry the relative reading, and what this rail
 * is for is the spacing between events — which only a clock can give.
 */
function AlertTimeline({
  alerts,
  places,
  onOpenAlert,
}: {
  alerts: HexAlert[];
  places?: Record<string, AlertPlace>;
  onOpenAlert?: (alert: HexAlert) => void;
}) {
  const rows = alerts.slice(0, TIMELINE_ROWS);
  if (rows.length === 0) return null;

  return (
    <div className="mt-3 border-t border-border pt-2.5">
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Timeline
      </p>
      <ol>
        {rows.map((alert, i) => {
          const sev = SEV[alert.severity] || SEV.normal;
          const place = places?.[alert.cameraId];
          const last = i === rows.length - 1;
          const name = alert.alertName || alert.eventType || 'Alert';
          const meta = [place?.name, place?.site, fmtClock(alert.timestamp), timeAgo(alert.timestamp)]
            .filter(Boolean)
            .join(' · ');
          /* A row opens the same record its card does — the rail is a second way into the
             alert, not a read-only caption of the row above it. Rows with no stored record
             yet can't be opened, and say so by not being a button. */
          const openable = !!onOpenAlert && !!alert.id;

          return (
            <li key={alert.id || `${alert.cameraId}-${alert.timestamp}`} className="flex gap-2.5">
              {/* The severity word, on the rail's left. Uppercase and small: a label FOR the
                  row, not part of it. */}
              <span
                className={`w-[52px] flex-none pt-[2px] text-right text-[9px] font-semibold uppercase leading-[15px] tracking-wide ${sev.text}`}
              >
                {sev.label}
              </span>

              {/* The rail: marker, then a hairline running to the next marker. Dropped on the
                  last row so the line ends ON an entry rather than trailing into the border. */}
              <span className="flex flex-none flex-col items-center self-stretch" aria-hidden>
                <span className={`mt-1 flex h-2.5 w-2.5 items-center justify-center rounded-full ${sev.dot}`}>
                  <span className="h-[3px] w-[3px] rounded-full bg-white" />
                </span>
                {!last && <span className="mt-0.5 w-px flex-1 bg-border" />}
              </span>

              <div className={`min-w-0 flex-1 ${last ? 'pb-0.5' : 'pb-2'}`}>
                {openable ? (
                  <button
                    type="button"
                    onClick={() => onOpenAlert?.(alert)}
                    title={name}
                    className="block max-w-full truncate rounded text-left text-[12px] font-medium leading-tight text-foreground transition-colors hover:text-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  >
                    {name}
                  </button>
                ) : (
                  <p className="truncate text-[12px] font-medium leading-tight text-foreground" title={name}>
                    {name}
                  </p>
                )}
                {meta && <p className="truncate text-[11px] leading-tight text-muted-foreground">{meta}</p>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * The badge's icon says WHAT KIND of thing tripped before the name is read — a person, a
 * vehicle, a fire — which is the distinction an operator triages on when two alerts share a
 * severity. Matched on the event type loosely (`person_detected`, `ppe-violation`,
 * `fire_smoke` are all the same family), falling back to the generic alert bell.
 */
const TYPE_ICONS: Array<[RegExp, typeof BellAlertIcon]> = [
  [/fire|smoke/, FireIcon],
  [/vehicle|anpr|plate|truck/, TruckIcon],
  [/ppe|helmet|vest/, ShieldExclamationIcon],
  [/theft|loiter/, HandRaisedIcon],
  [/fall/, ArrowTrendingDownIcon],
  [/face|person|people|intrusion|crowd|count/, UserIcon],
];

function typeIcon(alert: HexAlert): typeof BellAlertIcon {
  const key = `${alert.eventType || ''} ${alert.alertName || ''}`.toLowerCase();
  return TYPE_ICONS.find(([re]) => re.test(key))?.[1] || BellAlertIcon;
}

function fmtClock(ts?: number): string {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function timeAgo(ts?: number): string {
  if (!ts) return '';
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const s = Math.floor(Math.max(0, Date.now() - ms) / 1000);
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** Where the alert happened, as the operator names the place: camera, then site. */
export interface AlertPlace {
  name?: string;
  site?: string;
}

export interface LatestAlertsStripProps {
  /** Active alerts, newest first. As many as fit the row are drawn — see `minCardWidth`. */
  alerts: HexAlert[];
  /** Total open on the node — drives the pill and the "+N more" tile, and is ≥ alerts.length. */
  open: number;
  /** No feed yet: placeholders, never the empty state. */
  loading?: boolean;
  /** Narrowest a card may be, in px. The row fits as many of these as it can and then lets
   *  them share out the remainder, so the strip is flush at any window width. */
  minCardWidth?: number;
  /** Ceiling on the cards ONE SCREENFUL may show, however many would fit — the rest are a
   *  scroll away. Past about half a dozen at once the strip stops being a glance and becomes
   *  the alert list. */
  maxCards?: number;
  /** cameraId → where it is. Missing entries just render the alert without a place. */
  places?: Record<string, AlertPlace>;
  /** Lazy preview resolver for hosts whose snapshots aren't directly loadable (cloud). */
  loadPreview?: (alert: HexAlert) => Promise<string | null>;
  /** Open one alert's record. */
  onOpenAlert?: (alert: HexAlert) => void;
  /** Omit to hide the action — a node that can't acknowledge shouldn't offer to. */
  onAcknowledge?: (alert: HexAlert) => void;
  onResolve?: (alert: HexAlert) => void;
  /** The live board (honeycomb / floor plan) — the heading's link. Omit if unreachable. */
  onOpenBoard?: () => void;
  /**
   * The full alert LIST. Hung off the "N open" pill, which is the one thing on the strip that
   * is already about the whole queue rather than about a picture — and the slider took the
   * "+N more" tile that used to carry this. Omit and the pill is plain text.
   */
  onViewAll?: () => void;
  /**
   * Fetch the next page of older alerts. Called when the slider comes within one page of the
   * end of `alerts` while `open` says the node has more — never on mount, and never for a page
   * the operator has not nearly reached. Omit on hosts that can't page, and the slider simply
   * stops at whatever the live feed holds.
   */
  onLoadMore?: () => void;
  /** A page is in flight: the cells it will fill render as placeholders. */
  loadingMore?: boolean;
  className?: string;
}

/**
 * Shared shape for every alert cell, so a card and a placeholder are interchangeable and the
 * row's height never depends on which of them is showing.
 */
const CELL = 'relative aspect-[16/9] min-w-0 overflow-hidden rounded-xl';

/**
 * The cell's width, in the two states the row has.
 *
 * FITS: fewer alerts than the row holds, so nothing scrolls and the cards share the width out
 * — `flex-1` with a `max-w`, because one open alert on a wide monitor would otherwise inflate
 * into the video-wall this strip was deliberately shrunk out of.
 *
 * SCROLLS: more than fits, so every card is exactly one measured card wide (see `capacityFor`)
 * and the track has a constant pitch. A constant pitch is what lets the snap points, the
 * `4–6 of 13` readout and the scrubber all agree about where the row is.
 */
const CELL_FITS = 'w-full sm:flex-1 sm:max-w-[22rem]';
const CELL_SCROLLS = 'shrink-0 snap-start';

/** The open-count pill, which is a button when the host can open the list and plain text
 *  otherwise — identical either way, because the count is the same statement. */
const PILL =
  'rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide tabular-nums text-destructive';

/**
 * One edge cue: a strip pinned to a side of the row that fades the far card into the panel and
 * carries a chevron. Invisible until the row is hovered or something in it takes focus, and
 * never clickable itself — the strip is a full-height overlay, and the far card's acknowledge
 * and resolve chips live under its top corner. Only the knob takes a click.
 */
const EDGE =
  'pointer-events-none absolute inset-y-0 z-20 flex w-12 items-center rounded-xl from-card via-card/70 to-transparent opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100';

/** The chevron: the one target in the strip, vertically centred so it clears the chips at the
 *  card's top edge, and on the card surface so it reads against a bright snapshot as well as a
 *  dark one. Clickable only while the cue is actually showing — an invisible button parked over
 *  a photograph is a click the operator did not mean to make. */
const EDGE_KNOB =
  'pointer-events-none flex h-7 w-7 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-card transition-colors hover:bg-muted group-hover:pointer-events-auto group-focus-within:pointer-events-auto';

/** `gap-3` between cells, in px — the capacity arithmetic has to agree with the class. */
const GAP = 12;

/**
 * How many cards fit across `width`, given the narrowest a card may be.
 *
 * Measured rather than left to `flex-wrap` because the cards-per-page IS this number: the
 * slider's readout says which alerts of how many are on screen, and wrapping decides that
 * count inside the layout engine where this component can't read it. "4–6 of 13" beside four
 * cards on one monitor and two on another is worse than no readout at all.
 */
function capacityFor(width: number, minCardWidth: number): number {
  return Math.max(1, Math.floor((width + GAP) / (minCardWidth + GAP)));
}

function AlertMedia({
  alert,
  loadPreview,
  active,
}: {
  alert: HexAlert;
  loadPreview?: (alert: HexAlert) => Promise<string | null>;
  /** This card has come within a screen's edge of being looked at. Until then it costs no
   *  request at all — see the track's note on why every card in it is not a fetch. */
  active: boolean;
}) {
  const directUrl = alert.snapshotUrl || null;
  // 0 until the node says the snapshot is stored. Alerts are broadcast before the storage
  // lane has written the image, so "no picture yet" is the normal first state, not a failure.
  const previewReady = alert.previewReady || 0;
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);

  // Resolve lazily WITHOUT blocking the card, and revoke any object URL this created.
  useEffect(() => {
    setImgError(false);
    setResolvedUrl(null);
    if (directUrl || !loadPreview || !active) return;
    let cancelled = false;
    let made: string | null = null;
    loadPreview(alert)
      .then((url) => {
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        if (url) {
          made = url;
          setResolvedUrl(url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (made) URL.revokeObjectURL(made);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.id, directUrl, loadPreview, previewReady, active]);

  // A direct URL is gated the same way — on the local node it is a real request to the node's
  // snapshot store, and sixty of them to draw three cards is the same waste by another route.
  const src = !active || imgError ? null : directUrl || resolvedUrl;
  const Icon = typeIcon(alert);

  return (
    <>
      {/* The dark ground is painted whether or not an image lands, so the scrim, the badge and
          the white type below are legible from the first frame and don't flip contrast when
          the picture arrives. */}
      <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-neutral-950" />
      {src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center">
          {imgError ? (
            <PhotoIcon className="h-6 w-6 text-white/20" />
          ) : (
            <Icon className="h-6 w-6 animate-pulse text-white/25" />
          )}
        </span>
      )}
      {/* Bottom scrim only — a full overlay would grey the frame the operator is trying to
          read, and the top-row chips carry their own. */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/45 to-transparent" />
    </>
  );
}

export default function LatestAlertsStrip({
  alerts,
  open,
  loading = false,
  minCardWidth = 240,
  maxCards = 6,
  places,
  loadPreview,
  onOpenAlert,
  onAcknowledge,
  onResolve,
  onOpenBoard,
  onViewAll,
  onLoadMore,
  loadingMore = false,
  className = '',
}: LatestAlertsStripProps) {
  // "2m ago" is a lie the moment it's painted. One re-render a minute keeps the whole row
  // honest for the cost of a timer, and there is no cheaper place to put it than here.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // How much room the row actually has. Measured on the panel's BODY, which is present in
  // every state — measuring the row itself would lose the observer each time the strip passed
  // through its empty state and come back at zero, and measuring the outer panel would
  // over-count by the body's padding and fit one card too many.
  /**
   * WHICH SEVERITIES THE ROW IS SHOWING. Local state, not a prop: it selects among alerts the
   * strip already holds, so lifting it to the page would make every dashboard wire up a
   * control that changes nothing outside this panel.
   *
   * Filtering narrows what is DRAWN, never what is LOADED — the feed underneath stays whole,
   * so clearing the filter is instant and the paging arithmetic still has the real feed to
   * count against.
   */
  const [sev, setSev] = useState<SevFilter>('all');

  const rowRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = rowRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const obs = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /**
   * As many cards as fit, capped — and therefore the slider's page size. Before the first
   * measurement a single card is assumed: one card that widens is a smaller jolt than four
   * that vanish.
   */
  const capacity = useMemo(
    () => (width ? Math.min(capacityFor(width, minCardWidth), maxCards) : 1),
    [width, minCardWidth, maxCards],
  );

  /**
   * The scroll track, as an element AND as state.
   *
   * The element is what gets scrolled; the state is what the cards need — they gate their
   * preview fetch on coming into view, and "in view" means inside THIS box, not inside the
   * window. A ref alone can't tell them: it is still null on the render that mounts them.
   */
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [trackEl, setTrackEl] = useState<HTMLDivElement | null>(null);
  const setTrack = useCallback((el: HTMLDivElement | null) => {
    trackRef.current = el;
    setTrackEl(el);
  }, []);

  /** Where the track is, in px. Drives the readout, the scrubber and the end-of-track fetch —
   *  all three have to agree, so all three read the same measurement. */
  const [scroll, setScroll] = useState({ left: 0, view: 0, total: 0 });
  const readScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setScroll((prev) =>
      prev.left === el.scrollLeft && prev.view === el.clientWidth && prev.total === el.scrollWidth
        ? prev
        : { left: el.scrollLeft, view: el.clientWidth, total: el.scrollWidth },
    );
  }, []);

  /** One card, and one card plus its gap — the pitch every position readout is quoted in. */
  const cardW = width && capacity ? (width - (capacity - 1) * GAP) / capacity : 0;
  const pitch = cardW + GAP;

  const filtering = sev !== 'all';
  /** What the track actually draws. Identical to `alerts` unless a severity is selected. */
  const shown = useMemo(
    () => (sev === 'all' ? alerts : alerts.filter((a) => a.severity === sev)),
    [alerts, sev],
  );
  /** How many of each severity are in the loaded feed — the counts on the chips. */
  const counts = useMemo(() => {
    const c = { critical: 0, warning: 0, normal: 0 } as Record<HexAlert['severity'], number>;
    for (const a of alerts) if (a.severity in c) c[a.severity] += 1;
    return c;
  }, [alerts]);

  /**
   * The size of the queue the row is moving through — and it is NOT `open` once a severity is
   * chosen. `open` counts every open alert on the node, most of them a severity the row is no
   * longer drawing, so quoting it beside a filtered track would put "1–3 of 47" over three
   * criticals and read as forty-four more of them waiting off-screen.
   */
  const total = filtering ? shown.length : open;

  /** Nothing has arrived yet — placeholders, never the empty state: the two must not look
   *  the same, and `open` can land before the feed does. */
  const skeleton = loading && alerts.length === 0;
  const empty = !loading && shown.length === 0;
  /** Empty because of the CHIP, not because the node is quiet — a different sentence, and the
   *  only one of the two that has a way out of it. */
  const filteredOut = empty && alerts.length > 0;
  /** More alerts than the row holds, or a node with more to hand over: either way the row is
   *  a track rather than a static shelf. */
  const scrollable = !empty && !skeleton && (shown.length > capacity || shown.length < total);
  /**
   * Cells standing in for a page that is on its way, at the tail of the track. Only while a
   * fetch is actually in flight: a placeholder that persists because a request failed reads as
   * a broken card, and the readout already says how many alerts are unaccounted for.
   */
  const pending = loadingMore ? Math.max(0, Math.min(capacity, open - alerts.length)) : 0;

  // Re-measure whenever the track's contents or the available width change. Scrolling itself
  // is handled by the scroll event; this is for everything that resizes the track under it.
  useEffect(() => {
    readScroll();
  }, [readScroll, width, capacity, shown.length, pending, scrollable]);

  /** A new severity is a new queue: it starts at its newest alert, not at whatever pixel the
   *  previous one happened to be scrolled to. */
  useEffect(() => {
    const el = trackRef.current;
    if (el) el.scrollLeft = 0;
    readScroll();
  }, [sev, readScroll]);

  /**
   * FETCH AS THE TRACK RUNS OUT, not before.
   *
   * Two triggers, because there are two ways to reach the end of what is loaded: scrolling to
   * within a card of it, and a feed so short it cannot be scrolled at all (a node whose count
   * says twenty are open while its live feed handed over three). Without the second, that node
   * would sit at three alerts forever with no gesture available to ask for the rest.
   */
  useEffect(() => {
    if (!onLoadMore || loadingMore || alerts.length >= open) return;
    // `empty` counts as unscrollable and is checked FIRST: a filter that matched nothing
    // unmounts the track entirely, which leaves `scroll` holding the last measurement of a
    // track that no longer exists — the one state where the row most needs to fetch is the one
    // where the pixels would have said it didn't.
    const unscrollable = empty || scroll.total <= scroll.view + 1;
    const nearEnd = scroll.left + scroll.view >= scroll.total - Math.max(pitch, 1);
    // Scrolling to the end is the operator asking, and it is always answered.
    if (nearEnd) {
      onLoadMore();
      return;
    }
    // A track too short to scroll asks on its own — which under a severity filter is how a row
    // showing no criticals goes and finds some. Bounded, for the reason FILTER_SCAN_MAX gives.
    if (unscrollable && (!filtering || alerts.length < FILTER_SCAN_MAX)) onLoadMore();
  }, [onLoadMore, loadingMore, alerts.length, open, scroll, pitch, filtering, empty]);

  /** Which alerts are on screen, in the operator's terms: card-pitch arithmetic, so the
   *  readout moves one alert at a time as the track does. */
  const first = pitch ? Math.round(scroll.left / pitch) : 0;

  /** Is there anything past each edge? A one-pixel tolerance, because fractional widths and
   *  device pixel ratios mean a fully-scrolled track rarely lands on the exact number. */
  const moreBefore = scrollable && scroll.left > 1;
  const moreAfter = scrollable && scroll.left + scroll.view < scroll.total - 1;

  /**
   * The scrubber's thumb: as wide a fraction of the track as the visible part is of the whole,
   * positioned by how far through the scrollable range the row has come. A floor on the width,
   * because a thumb representing three of sixty alerts is a sliver too small to grab.
   */
  const maxScroll = Math.max(0, scroll.total - scroll.view);
  const thumb = scroll.total > 0 ? Math.max(0.1, Math.min(1, scroll.view / scroll.total)) : 1;
  const thumbAt = maxScroll > 0 ? (scroll.left / maxScroll) * (1 - thumb) : 0;

  /** Drag the scrubber, or click anywhere along it. The thumb's CENTRE follows the pointer,
   *  which is what makes the two ends reachable without overshooting. */
  const scrubbing = useRef(false);
  const scrubTo = useCallback(
    (clientX: number, rail: HTMLElement) => {
      const el = trackRef.current;
      if (!el || maxScroll <= 0) return;
      const r = rail.getBoundingClientRect();
      const thumbPx = r.width * thumb;
      const span = Math.max(1, r.width - thumbPx);
      const f = Math.max(0, Math.min(1, (clientX - r.left - thumbPx / 2) / span));
      el.scrollLeft = f * maxScroll;
    },
    [maxScroll, thumb],
  );

  /** A screen at a time for the arrow keys — the same distance the scrubber's thumb covers,
   *  so keyboard and pointer move the row in the same units. */
  const nudge = useCallback((delta: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: delta * Math.max(el.clientWidth, 1), behavior: 'smooth' });
  }, []);

  return (
    <section className={`rounded-xl border border-border bg-card shadow-card ${className}`}>
      {/* The panel's head, on the same rule as the other sheets' — a hairline under the title
          rather than a gap, so the heading reads as this row's label and not as a stray line
          of page text. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-2">
          <h2 className="text-[13px] font-semibold text-foreground">Alerts dashboard</h2>
          {open > 0 &&
            /* The pill is the queue's own control now that the "+N more" tile is gone: it is
               already a statement about all of them rather than about any one picture, and
               clicking a count to get the list is what a count looks like everywhere else. */
            (onViewAll ? (
              <button
                type="button"
                onClick={onViewAll}
                title="View all open alerts"
                className={`${PILL} transition-colors hover:bg-destructive hover:text-white`}
              >
                {open} open
              </button>
            ) : (
              <span className={PILL}>{open} open</span>
            ))}
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          {/*
            THE SEVERITY FILTER. A segmented track rather than a select, on the same reasoning
            the board's filter row uses: the options are half the information here. "Critical 2"
            is read before anything is clicked, and behind a dropdown that number would only
            appear after the operator had already decided to go looking for it.

            Single-choice with an always-lit "All", so the row can never be in a state whose
            meaning has to be worked out from which chips AREN'T on — and All is the way back,
            which is why there is no separate clear.

            Hidden while the feed is still arriving: chips reading zero next to a row of
            placeholders say "no criticals" about a strip that has not been told anything yet.
          */}
          {!skeleton && alerts.length > 0 && (
            <div
              role="radiogroup"
              aria-label="Filter alerts by severity"
              className="inline-flex flex-none items-center gap-0.5 rounded-lg border border-border bg-card p-0.5"
            >
              {SEV_FILTERS.map((f) => {
                const active = f.key === sev;
                const count = f.key === 'all' ? alerts.length : counts[f.key];
                return (
                  <button
                    key={f.key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setSev(f.key)}
                    title={
                      f.key === 'all'
                        ? 'Every open alert'
                        : `${f.label} only — ${count} loaded${count === 0 ? ', the row will look further back' : ''}`
                    }
                    /* Selected is the slate pill SegmentedToggle uses, NOT a severity fill: a
                       chip flooded red for "Critical" would be the loudest red on a panel whose
                       whole job is to make the alert cards' own red the thing you see first. */
                    className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold transition-colors ${
                      active
                        ? 'bg-muted text-foreground shadow-sm ring-1 ring-inset ring-border'
                        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                    }`}
                  >
                    {f.dot && (
                      <span
                        aria-hidden
                        /* Dimmed at zero, so "none of these" is legible from the chip alone
                           without it going quiet enough to look disabled — it is still the
                           chip that goes and looks further back. */
                        className={`h-1.5 w-1.5 flex-none rounded-full ${f.dot} ${count === 0 ? 'opacity-40' : ''}`}
                      />
                    )}
                    <span className="whitespace-nowrap">{f.label}</span>
                    <span className={`tabular-nums ${active ? 'text-muted-foreground' : 'opacity-70'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* WHICH of them is on screen. Only where it says something: a row showing
              everything that is open has no position to report. */}
          {scrollable && (
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {first + 1}–{Math.min(first + capacity, total)} of {total}
            </span>
          )}
          {onOpenBoard && (
            /* Transparent, not `bg-card`: the button now sits ON the card, and a card-coloured
               fill there is invisible. The border and the hover are what make it a control. */
            <button
              type="button"
              onClick={onOpenBoard}
              className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
            >
              Alert board
              <ArrowRightIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div ref={rowRef} className="p-3">
        {empty ? (
          /* Deliberately one line, not two empty frames: an all-clear machine should read as
             calm. The board link above still gets the operator to the history. Filled a shade
             off the card it sits on, since a card-on-card panel has no edge at all. */
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border bg-muted/40 px-4 py-3 text-[13px] text-muted-foreground">
            {filteredOut ? (
              <>
                {/* NOT the all-clear tick: the node is not clear, the operator is looking at a
                    slice of it, and a green check over a queue holding warnings is a lie. What
                    the line owes them is which slice is empty, whether the strip is still
                    looking, and one click back to everything. */}
                <FunnelIcon className="h-4 w-4" aria-hidden />
                <span>
                  No {SEV_FILTERS.find((f) => f.key === sev)?.label.toLowerCase()} alerts among the{' '}
                  {alerts.length} loaded
                  {loadingMore ? ' — looking further back…' : ''}.
                </span>
                <button
                  type="button"
                  onClick={() => setSev('all')}
                  className="rounded-md border border-border px-2 py-0.5 text-[12px] font-medium text-foreground transition-colors hover:bg-muted"
                >
                  Show all
                </button>
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                No open alerts — nothing needs attention right now.
              </>
            )}
          </div>
        ) : (
          <>
            {/* `group` so the edge cues can answer a hover anywhere over the row, and
                `relative` so they can be pinned to its edges rather than to a card — the far
                card changes as the row scrolls, the edge doesn't. */}
            <div className="group relative">
              {/*
                THE TRACK. Native horizontal scroll, so a trackpad swipe, a touch drag and a
                shift-wheel all work without a line of code — and snap points at the card pitch,
                so it comes to rest on a whole card rather than halfway through a face.

                The browser's own scrollbar is hidden: it would sit right under the row saying
                roughly what the scrubber below says exactly, and at the 5px this app styles it
                to, it reads as an artefact rather than a control.
              */}
              <div
                ref={setTrack}
                role="group"
                aria-label="Alerts dashboard"
                tabIndex={scrollable ? 0 : -1}
                onScroll={readScroll}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowRight') { e.preventDefault(); nudge(1); }
                  if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(-1); }
                }}
                className={`flex gap-3 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
                  scrollable
                    ? 'snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
                    : 'flex-col overflow-hidden sm:flex-row sm:items-stretch'
                }`}
              >
                {skeleton
                  ? Array.from({ length: capacity }, (_, i) => (
                      <div key={`skeleton-${i}`} className={`${CELL} ${CELL_FITS} animate-pulse bg-muted`} />
                    ))
                  : shown.map((alert) => (
                      <AlertTile
                        key={alert.id || `${alert.cameraId}-${alert.timestamp}`}
                        alert={alert}
                        place={places?.[alert.cameraId]}
                        loadPreview={loadPreview}
                        onOpen={onOpenAlert}
                        onAcknowledge={onAcknowledge}
                        onResolve={onResolve}
                        /* Fixed pitch once the row scrolls; sharing the width out when it
                           doesn't. Width in px rather than a percentage because the track is
                           wider than its own box, and a percentage would resolve against the
                           wrong one. */
                        cellClass={scrollable ? CELL_SCROLLS : CELL_FITS}
                        cellStyle={scrollable && cardW ? { width: cardW } : undefined}
                        /* Previews resolve against THIS box coming into view, not the window. */
                        viewport={trackEl}
                      />
                    ))}

                {/* The page on its way. Same size and surface as a card, so nothing reflows
                    when it lands. */}
                {!skeleton &&
                  Array.from({ length: pending }, (_, i) => (
                    <div
                      key={`pending-${i}`}
                      className={`${CELL} ${scrollable ? CELL_SCROLLS : CELL_FITS} animate-pulse bg-muted`}
                      style={scrollable && cardW ? { width: cardW } : undefined}
                    />
                  ))}
              </div>

              {/*
                THE EDGE CUES. One per side, and only on the side that has alerts behind it —
                so the right-hand one appearing IS the statement that the card under it is not
                the last alert. The fade is the card surface itself, so the picture reads as
                continuing under the panel rather than as having been cropped.
              */}
              {moreBefore && (
                <div aria-hidden className={`${EDGE} left-0 justify-start bg-gradient-to-r pl-1.5`}>
                  <button
                    type="button"
                    aria-label="Scroll to newer alerts"
                    onClick={() => nudge(-1)}
                    className={EDGE_KNOB}
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
              {moreAfter && (
                <div aria-hidden className={`${EDGE} right-0 justify-end bg-gradient-to-l pr-1.5`}>
                  <button
                    type="button"
                    aria-label="Scroll to older alerts"
                    onClick={() => nudge(1)}
                    className={EDGE_KNOB}
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {/*
              THE SCRUBBER — the STANDING indication, the one that is true without being asked
              for, and a control in its own right: the thumb's width is the share of the queue on
              screen and its position is where in the queue that share sits, so "thirteen open, I
              am looking at three near the front" is one glance rather than a count to be worked
              out. The edge cues above answer a hover; this answers a look.

              A real slider, hence `role="slider"` and the pointer capture: click anywhere to
              jump there, drag to sweep. Keyboard is deliberately NOT here but on the track,
              which is the thing being moved — two focus stops that scroll the same row by the
              same arrow keys would just be two ways to lose your place.
            */}
            {scrollable && maxScroll > 0 && (
              <div
                role="slider"
                aria-label="Scroll alerts"
                aria-valuemin={0}
                aria-valuemax={Math.max(0, total - capacity)}
                aria-valuenow={first}
                aria-valuetext={`Alerts ${first + 1} to ${Math.min(first + capacity, total)} of ${total}`}
                onPointerDown={(e) => {
                  scrubbing.current = true;
                  e.currentTarget.setPointerCapture(e.pointerId);
                  scrubTo(e.clientX, e.currentTarget);
                }}
                onPointerMove={(e) => {
                  if (scrubbing.current) scrubTo(e.clientX, e.currentTarget);
                }}
                onPointerUp={() => { scrubbing.current = false; }}
                onPointerCancel={() => { scrubbing.current = false; }}
                className="group relative mt-2.5 h-3 cursor-pointer touch-none"
              >
                {/* The rail is thin; the box around it is finger-sized. */}
                <span className="absolute inset-x-0 top-1 h-1 rounded-full bg-muted" />
                <span
                  className="absolute top-1 h-1 rounded-full bg-muted-foreground/50 transition-colors group-hover:bg-muted-foreground"
                  style={{ width: `${thumb * 100}%`, left: `${thumbAt * 100}%` }}
                />
              </div>
            )}

            {/* THE SEQUENCE, under the pictures. Same list, same filter — see AlertTimeline. */}
            <AlertTimeline alerts={shown} places={places} onOpenAlert={onOpenAlert} />
          </>
        )}
      </div>
    </section>
  );
}

function AlertTile({
  alert,
  place,
  loadPreview,
  onOpen,
  onAcknowledge,
  onResolve,
  cellClass,
  cellStyle,
  viewport,
}: {
  alert: HexAlert;
  place?: AlertPlace;
  loadPreview?: (alert: HexAlert) => Promise<string | null>;
  onOpen?: (alert: HexAlert) => void;
  onAcknowledge?: (alert: HexAlert) => void;
  onResolve?: (alert: HexAlert) => void;
  /** How this cell is sized — see CELL_FITS / CELL_SCROLLS. */
  cellClass: string;
  cellStyle?: CSSProperties;
  /** The scroll box to judge "in view" against. Null falls back to the window, which is only
   *  right for a row that doesn't scroll. */
  viewport: Element | null;
}) {
  const sev = SEV[alert.severity || 'normal'] || SEV.normal;
  const Icon = typeIcon(alert);
  const name = alert.alertName || alert.eventType || 'Alert';
  // Camera · site · age, with the blanks dropped rather than left as stray separators.
  const where = [place?.name, place?.site, timeAgo(alert.timestamp)].filter(Boolean).join(' • ');
  const actionable = !!alert.id;

  /**
   * Has this card been looked at yet?
   *
   * One observer per cell, disconnected the moment it fires: the picture is fetched once and
   * KEPT, so sweeping back over the same alerts costs nothing and a card can't flicker between
   * its image and its placeholder as it crosses the edge of the track. A screen's worth of
   * margin means the next card along is already resolving as the current one is being read.
   */
  const cellRef = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen) return;
    const el = cellRef.current;
    // No observer (old browser, jsdom): fall back to loading it, which is the old behaviour.
    if (!el || typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setSeen(true);
      },
      { root: viewport, rootMargin: '0px 300px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [seen, viewport]);

  return (
    <div ref={cellRef} style={cellStyle} className={`${CELL} ${cellClass} bg-neutral-900 ring-1 ${sev.ring} shadow-card`}>
      <AlertMedia alert={alert} loadPreview={loadPreview} active={seen} />

      {/* The whole card opens the alert — a full-bleed button BEHIND the chips rather than a
          wrapper around them, because a button inside a button is invalid HTML and the
          browser resolves it by dropping one. The chips sit above this layer and stop the
          click themselves. */}
      {onOpen && (
        <button
          type="button"
          aria-label={`Open ${name}`}
          title="Open alert"
          className="absolute inset-0 z-0"
          onClick={() => onOpen(alert)}
        />
      )}

      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-1.5 p-1.5">
        <span
          className={`pointer-events-auto flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${sev.badge}`}
          title={`${sev.label} — ${(alert.eventType || '').replace(/[_-]+/g, ' ')}`.trim()}
        >
          <Icon className="h-3 w-3" />
          {sev.label}
        </span>

        <span className="flex items-center gap-1">
          {alert.timestamp != null && (
            <span className="rounded bg-black/60 px-1.5 py-0.5 font-mono text-[10px] font-medium tabular-nums text-white">
              {fmtClock(alert.timestamp)}
            </span>
          )}
          {actionable && onAcknowledge && (
            <Tooltip label="Acknowledge — I'm on it" side="bottom" className="pointer-events-auto">
              <button
                type="button"
                aria-label="Acknowledge alert"
                onClick={() => onAcknowledge(alert)}
                className="pointer-events-auto rounded bg-black/60 p-1 text-white transition-colors hover:bg-emerald-600"
              >
                <CheckIcon className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          )}
          {actionable && onResolve && (
            <Tooltip label="Resolve — dealt with" side="bottom" className="pointer-events-auto">
              <button
                type="button"
                aria-label="Resolve alert"
                onClick={() => onResolve(alert)}
                className="pointer-events-auto rounded bg-black/60 p-1 text-white transition-colors hover:bg-emerald-600"
              >
                <CheckCircleIcon className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          )}
        </span>
      </div>

      {/* WHAT, then WHERE and WHEN — the order the alert is triaged in, and the reason the
          name gets the page's largest type below the heading. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 p-2">
        <p className="truncate text-[13px] font-semibold leading-tight text-white" title={name}>
          {name}
        </p>
        {where && (
          <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-white/75" title={where}>
            <VideoCameraIcon className="h-3 w-3 shrink-0" />
            <span className="truncate">{where}</span>
          </p>
        )}
      </div>
    </div>
  );
}
